// EVA-303. The waterfall's arithmetic, with no React and no DOM in it.
//
// It lives here rather than inside the component because on a valuation
// product THE BAR HEIGHTS ARE THE CLAIM: a chart that draws a correct number at
// the wrong height is the same class of defect as a mis-attributed driver — it
// looks right and nothing flags it. EVA's only JavaScript test path is
// spec/javascript shelling out to bare node against pure ESM, so geometry that
// lives in JSX is geometry nothing can assert.
//
// Every number below is derived from the prototype's own layout module, whose
// tests pin the same behaviour.

// Plot box. Fixed height; the width comes from the container.
//
// The SVG stops just under the axis line. Category labels are HTML positioned
// against the same pixel grid rather than <text> — SVG has no wrapping and no
// ellipsis, and these labels are phrases ("Market Rent & Re-letting"), not
// numbers. Value labels stay in the SVG, where they belong to their bar.
export const HEIGHT = 232
export const PLOT_TOP = 28
export const PLOT_BOTTOM = 220
export const DEFAULT_WIDTH = 1146
const SIDE_MARGIN = 28
const MINIMUM_SLOT_WIDTH = 88

// A movement bar is a proportion of its slot, clamped so it never becomes a
// hairline on a crowded bridge nor a slab on a sparse one. Totals are wider, to
// read as bookends rather than as another movement.
const MOVEMENT_BAR_RATIO = 0.58
const MINIMUM_BAR_WIDTH = 42
const MAXIMUM_BAR_WIDTH = 68
const TOTAL_BAR_RATIO = 1.14
const TOTAL_SLOT_CAP = 0.72

// A movement of nothing still gets a visible mark, and every interactive mark
// gets a 32px hit box centred on it whatever its size — a 2px bar is not a
// click target.
const MINIMUM_BAR_HEIGHT = 2
const MINIMUM_HIT_SIZE = 32

// The domain is adaptive, never forced to zero. An opening of 127m with a 200k
// movement must still SHOW the movement, so the scale covers the cumulative
// levels the bridge actually visits, padded.
//
// Two terms, and the larger wins. The range term keeps a busy bridge from
// filling the plot edge to edge; the magnitude term stops a single small
// movement from being blown up into a microscope. The floor of 1 currency unit
// keeps a degenerate bridge from collapsing to a zero-height domain.
const CUMULATIVE_RANGE_PADDING_RATIO = 0.25
const VALUE_MAGNITUDE_PADDING_RATIO = 0.025
const MINIMUM_PADDING = 1

// Money crosses the wire as a decimal STRING or null, never a number — null
// means "unavailable" and is not 0.00 (money-and-dates.md). Anything that is
// not a finite number stays null rather than becoming zero.
export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// Cumulative levels for the movement bars, walking from the opening total.
// Each movement starts where the previous one ended, which is what makes the
// step read without a connector.
export function cumulate(bars) {
  // The opening total is whichever total comes first — not the id 'opening',
  // which would make the arithmetic depend on a serializer label.
  const opening = bars.find((bar) => bar.kind === 'total')
  let cursor = toNumber(opening && opening.amount) ?? 0

  return bars.map((bar) => {
    const amount = toNumber(bar.amount)
    if (bar.kind === 'total') {
      return { ...bar, amount, startValue: null, endValue: amount }
    }
    const startValue = cursor
    const endValue = startValue + (amount ?? 0)
    cursor = endValue
    return { ...bar, amount, startValue, endValue }
  })
}

// Only the levels the bridge actually visits. A total contributes its own value
// and NOT a zero base — that is the mechanism that keeps zero out of the domain.
export function domainFor(levelled) {
  const levels = []
  levelled.forEach((bar) => {
    if (bar.endValue !== null) levels.push(bar.endValue)
    if (bar.kind !== 'total' && bar.startValue !== null) levels.push(bar.startValue)
  })
  if (levels.length === 0) return null

  const minimum = Math.min(...levels)
  const maximum = Math.max(...levels)
  const range = maximum - minimum
  const magnitude = Math.max(Math.abs(minimum), Math.abs(maximum), 1)

  const padding = range > magnitude * Number.EPSILON
    ? Math.max(range * CUMULATIVE_RANGE_PADDING_RATIO, magnitude * VALUE_MAGNITUDE_PADDING_RATIO, MINIMUM_PADDING)
    : Math.max(magnitude * VALUE_MAGNITUDE_PADDING_RATIO, MINIMUM_PADDING)

  return { minimum: minimum - padding, maximum: maximum + padding, padding }
}

// The whole layout, from the serializer's bars to pixel rectangles.
//
// Returns `{ available: false }` when there is nothing honest to draw: with no
// rounding policy configured the endpoints have no rounded value, and a
// waterfall between two unknowns is a picture of nothing. The table below still
// renders — the figures are not lost, only the chart.
export function layout(bars, availableWidth = DEFAULT_WIDTH) {
  const drawable = (bars || []).filter((bar) => toNumber(bar.amount) !== null)
  const totals = drawable.filter((bar) => bar.kind === 'total')
  if (drawable.length === 0 || totals.length < 2) {
    return { available: false, reason: 'no_rounded_endpoints' }
  }

  const levelled = cumulate(drawable)
  const domain = domainFor(levelled)
  if (!domain || domain.maximum === domain.minimum) {
    return { available: false, reason: 'no_range' }
  }

  // EVA-307: a bar the caller marked is WALKED but not DRAWN — `cumulate` and
  // `domainFor` above still see it. That ordering is the point: the levels the
  // bridge visits do not change because a step of nothing went unpainted, so
  // every bar still sits where its own cumulative level puts it and the last
  // movement still lands on the closing total. Filtering before the walk would
  // instead shift every bar after the omission by the amount omitted.
  //
  // `chartBars` never marks a total, so `shown` cannot empty out today. The
  // guard is here because this module cannot check that from its side, and
  // `Math.max(…, 1)` below protects the division and not the drawing: without
  // it a fully marked bridge would render an axis, an empty label strip and the
  // broken-axis note, which claims a picture where there is none.
  const shown = levelled.filter((bar) => !bar.omitted)
  if (shown.length === 0) return { available: false, reason: 'nothing_to_draw' }

  const slots = Math.max(shown.length, 1)
  const minimumCanvasWidth = SIDE_MARGIN * 2 + MINIMUM_SLOT_WIDTH * slots
  const canvasWidth = Math.max(Math.floor(availableWidth) || 0, minimumCanvasWidth)
  const slotWidth = (canvasWidth - SIDE_MARGIN * 2) / slots
  const movementBarWidth = Math.min(
    Math.max(slotWidth * MOVEMENT_BAR_RATIO, MINIMUM_BAR_WIDTH),
    MAXIMUM_BAR_WIDTH
  )
  const totalBarWidth = Math.min(movementBarWidth * TOTAL_BAR_RATIO, slotWidth * TOTAL_SLOT_CAP)

  const plotHeight = PLOT_BOTTOM - PLOT_TOP
  const project = (value) =>
    PLOT_TOP + ((domain.maximum - value) / (domain.maximum - domain.minimum)) * plotHeight

  const drawn = shown.map((bar, index) => {
    const total = bar.kind === 'total'
    const barWidth = total ? totalBarWidth : movementBarWidth
    const center = SIDE_MARGIN + slotWidth * index + slotWidth / 2

    // A total is drawn from the FLOOR OF THE DOMAIN, not from zero — which is
    // why the axis is broken and the component must say so.
    const from = total ? domain.minimum : bar.startValue
    const to = bar.endValue
    const top = Math.min(project(from), project(to))
    const measuredHeight = Math.abs(project(from) - project(to))
    const height = Math.max(measuredHeight, MINIMUM_BAR_HEIGHT)

    const hitWidth = Math.max(barWidth, MINIMUM_HIT_SIZE)
    const hitHeight = Math.max(measuredHeight, MINIMUM_HIT_SIZE)

    return {
      ...bar,
      index,
      x: center - barWidth / 2,
      y: top,
      width: barWidth,
      height,
      centerX: center,
      hitX: center - hitWidth / 2,
      hitY: top - (hitHeight - height) / 2,
      hitWidth,
      hitHeight,
      // Sign drives colour and which side the value label sits. Zero labels
      // above, like a positive one.
      sign: bar.amount === null || bar.amount === 0 ? 'zero' : bar.amount > 0 ? 'positive' : 'negative',
      labelSide: total || (bar.amount ?? 0) >= 0 ? 'above' : 'below',
      labelY: total || (bar.amount ?? 0) >= 0
        ? Math.max(PLOT_TOP + 12, top - 7)
        : Math.min(PLOT_BOTTOM - 4, top + height + 15)
    }
  })

  return {
    available: true,
    axisBroken: true,
    canvasWidth,
    slotWidth,
    height: HEIGHT,
    plotTop: PLOT_TOP,
    plotBottom: PLOT_BOTTOM,
    domain,
    bars: drawn
  }
}
